import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { throttle } from 'lodash';
import { useIntl } from 'react-intl';

import { useListMonitoringGateways } from 'src/features/monitoring/api/useListMonitoringGateways';
import GatewayActionModal from 'src/features/monitoring/components/GatewayActionModal';
import SingleMonitoringGateway from 'src/features/monitoring/components/SingleMonitoringGateway';
import { parkingStatusMessages } from 'src/features/monitoring/utils/messages';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { GatewayActionState, WebSocketEvent, WebSocketEventMessages } from 'src/features/visitor/types';
import { SectionBlock, SectionBlockBody } from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import MonitoringTabRow from 'src/features/monitoring/components/MonitoringTabRow';

export default function MonitoringGateways() {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const [isOpen, setIsOpen] = React.useState(true);
    const [visitorKeyPairs, setVisitorKeyPairs] = React.useState<{
        [key: string]: {
            currentLicensePlate?: string;
            pastVisitorId?: string;
        };
    }>({});
    const [gatewayKeyPairs, setGatewayKeyPairs] = React.useState<{ [key: string]: boolean }>({});
    const [gatewayAction, setGatewayAction] = React.useState<GatewayActionState>(null);

    const { data, isLoading } = useListMonitoringGateways({ parkingId });
    const { subscribe, unsubscribe } = React.useContext(WSContext);

    const handleWebSocket = React.useMemo(
        () =>
            throttle((event: WebSocketEvent) => {
                if (
                    event.message === WebSocketEventMessages.NEW_ENTRY_PASS ||
                    event.message === WebSocketEventMessages.NEW_EXIT_PASS
                ) {
                    setVisitorKeyPairs((prevState) => ({
                        ...prevState,
                        [event.data.gateway_id]: {
                            pastVisitorId: event.data.visitor_id,
                        },
                    }));
                    setGatewayKeyPairs((prevState) => ({ ...prevState, [event.data.gateway_id]: false }));
                } else if (event.message === WebSocketEventMessages.NEW_LPR_DETECTION) {
                    setVisitorKeyPairs((prevState) => ({
                        ...prevState,
                        [event.data.gateway_id]: {
                            ...prevState[event.data.gateway_id],
                            currentLicensePlate: event.data.license_plate,
                        },
                    }));
                    setGatewayKeyPairs((prevState) => ({ ...prevState, [event.data.gateway_id]: true }));
                }
            }, 500),
        [],
    );

    React.useEffect(() => {
        subscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);

        return () => unsubscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);
    }, [handleWebSocket, subscribe, unsubscribe]);

    return (
        <>
            <GatewayActionModal gatewayAction={gatewayAction} setGatewayAction={setGatewayAction} />
            <div className="flex items-center justify-between">
                <MonitoringTabRow />
                <div className="flex justify-end">
                    <Button variant="outline" onClick={() => setIsOpen((prevState) => !prevState)}>
                        <FontAwesomeIcon icon={isOpen ? ICON_MAP.COMPRESS : ICON_MAP.EXPAND} />
                        {formatMessage(isOpen ? parkingStatusMessages.collapseAll : parkingStatusMessages.expandAll)}
                    </Button>
                </div>
            </div>
            <SectionBlock>
                <SectionBlockBody className="grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {isLoading &&
                        Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton key={index} className="h-160 rounded-xl" />
                        ))}
                    {data?.results.map((gateway, index: number) => (
                        <SingleMonitoringGateway
                            index={index}
                            key={gateway.id}
                            gateway={gateway}
                            isOpen={isOpen}
                            isBusy={gatewayKeyPairs[gateway.id]}
                            visitors={visitorKeyPairs[gateway.id]}
                            setGatewayAction={setGatewayAction}
                        />
                    ))}
                </SectionBlockBody>
            </SectionBlock>
        </>
    );
}
